import { useDispatch, useSelector } from 'react-redux';
import { selectActiveSuggestions } from '../../redux/features/aiSuggestions/aiSuggestions.selector';
import { dismissSuggestion } from '../../redux/features/aiSuggestions/aiSuggestions.slice';
import { IAISuggestion } from '../../redux/features/aiSuggestions/aiSuggestions.props';
import useSendMessageHooks from '../../hooks/useSendMessage/useSendMessage.hooks';
import useAgentInterception from '../../hooks/agentInterception/useAgentInterception.hooks';
import {
  AgentInterceptionType,
  messagingActions,
} from '../../reducers/messagingSlice';
import { selectFeatureFlagHitl3 } from '../../redux/features/config/config.selector';
import logger from '../../utils/logger';

/**
 * Accept / dismiss logic for AI suggestions, extracted so the card buttons and
 * the Alt+A / Alt+D keyboard shortcuts drive the exact same path. The human
 * agent stays the driver — a suggestion is only ever applied on an explicit
 * accept.
 */
const useAISuggestionActions = () => {
  const dispatch = useDispatch();
  const suggestions = useSelector(selectActiveSuggestions);
  const isHitl3 = useSelector(selectFeatureFlagHitl3);
  const { sendMessage } = useSendMessageHooks();
  const { sendAgentInterception } = useAgentInterception();

  // Only the latest suggestion is ever displayed; treat it as the active one
  // that the keyboard shortcuts act on.
  const activeSuggestion: IAISuggestion | undefined =
    suggestions[suggestions.length - 1];

  /**
   * Emit the HITL -> AI response the backend consumes to close the co-pilot
   * loop. hitl_3_0 only. The envelope (event/agent_type/session_id) is stamped
   * by the messaging middleware; we only supply `data`. `verbatim` is always
   * true today — the accepted line is spoken unchanged (no edit-before-speak
   * flow yet).
   */
  const emitSuggestionResponse = (
    suggestion: IAISuggestion,
    action: 'accepted' | 'dismissed'
  ) => {
    if (!isHitl3) return;
    dispatch(
      messagingActions.sendAgentSuggestionResponse({
        data: {
          action,
          suggestion_id: suggestion.suggestion_id,
          request_id: suggestion.request_id,
          ...(action === 'accepted' && suggestion.tts
            ? { spoken_text: suggestion.tts, verbatim: true }
            : null),
        },
      })
    );
  };

  const accept = (suggestion: IAISuggestion) => {
    if (suggestion.tts) {
      // Any suggestion that carries a `tts` line is speakable, regardless of its
      // suggestion_type. Speak it via the normal HITL send path (status=final
      // -> TTS), and mark the agent as having taken the turn (TTS_SENT), exactly
      // like an intent pill / OmniBar send.
      sendAgentInterception({
        data: { type: AgentInterceptionType.TTS_SENT },
      });
      sendMessage(suggestion.tts, { intent: 'ai_suggestion' });
      logger.log({
        message: `AI suggestion accepted (tts): "${suggestion.tts}" (${suggestion.suggestion_id})`,
      });
    } else {
      // TODO (step 2): map suggestion.action -> a cartSlice mutation
      // (selectModifier / addItemToCart) for cart-action suggestions.
      logger.log({
        message: `AI suggestion accepted: ${suggestion.suggestion_type} (${suggestion.suggestion_id})`,
        moreInfo: JSON.stringify(suggestion.action),
      });
    }
    emitSuggestionResponse(suggestion, 'accepted');
    dispatch(
      dismissSuggestion({
        suggestionType: suggestion.suggestion_type,
        suggestionId: suggestion.suggestion_id,
      })
    );
  };

  const dismiss = (suggestion: IAISuggestion) => {
    logger.log({
      message: `AI suggestion dismissed: ${suggestion.suggestion_type} (${suggestion.suggestion_id})`,
    });
    emitSuggestionResponse(suggestion, 'dismissed');
    dispatch(
      dismissSuggestion({
        suggestionType: suggestion.suggestion_type,
        suggestionId: suggestion.suggestion_id,
      })
    );
  };

  return { suggestions, activeSuggestion, accept, dismiss };
};

export default useAISuggestionActions;
